// Onboarding persistence is intentionally localStorage for now; a later move to
// per-user backend touches only this file.

import { useCallback, useMemo } from 'react';
import packageJson from '../../package.json';
import { CHANGELOG, ChangelogEntry } from '../data/changelog';

export const LAST_SEEN_VERSION_KEY = 'salty_lastSeenVersion';
export const TOUR_COMPLETED_KEY = 'salty_tourCompleted';

const CURRENT_VERSION = packageJson.version;

// Safe localStorage access — Safari private mode / disabled storage can throw on
// read or write. We swallow the error and fall back to in-memory defaults so the
// app never crashes because of onboarding state.
function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore — storage unavailable
  }
}

/**
 * Compares two dotted version strings (e.g. '1.0.0' vs '0.9.0').
 * Returns a negative number if a < b, positive if a > b, and 0 if equal.
 * Missing segments are treated as 0 and non-numeric segments as 0.
 */
function compareVersions(a: string, b: string): number {
  const pa = a.split('.');
  const pb = b.split('.');
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i += 1) {
    const na = parseInt(pa[i] ?? '0', 10) || 0;
    const nb = parseInt(pb[i] ?? '0', 10) || 0;
    if (na !== nb) return na - nb;
  }
  return 0;
}

/**
 * Returns the CHANGELOG entries the user has NOT seen yet — everything strictly
 * newer than lastSeen.
 *
 * - If lastSeen is null (brand-new user) returns [] — first run is handled
 *   elsewhere (tour, not What's New).
 * - If lastSeen matches an entry's version, returns the entries above it.
 * - If lastSeen is not in the list (an older version that predates the oldest
 *   listed entry, e.g. '0.9.0'), returns every entry newer than it by semver.
 *   Only version-bump callers reach this path (lastSeen !== current), so this
 *   cannot spam an up-to-date user.
 */
export function entriesSince(lastSeen: string | null): ChangelogEntry[] {
  if (lastSeen == null) return [];
  const idx = CHANGELOG.findIndex((entry) => entry.version === lastSeen);
  if (idx !== -1) return CHANGELOG.slice(0, idx);
  // lastSeen isn't a listed version — return everything strictly newer than it.
  return CHANGELOG.filter((entry) => compareVersions(entry.version, lastSeen) > 0);
}

export interface OnboardingState {
  currentVersion: string;
  lastSeenVersion: string | null;
  tourCompleted: boolean;
  markVersionSeen: () => void;
  markTourCompleted: () => void;
  isFirstRun: boolean;
  hasVersionBump: boolean;
}

export function useOnboardingState(): OnboardingState {
  const lastSeenVersion = safeGet(LAST_SEEN_VERSION_KEY);
  const tourCompleted = safeGet(TOUR_COMPLETED_KEY) === 'true';

  const markVersionSeen = useCallback(() => {
    safeSet(LAST_SEEN_VERSION_KEY, CURRENT_VERSION);
  }, []);

  const markTourCompleted = useCallback(() => {
    safeSet(TOUR_COMPLETED_KEY, 'true');
  }, []);

  const isFirstRun = lastSeenVersion == null;
  const hasVersionBump = lastSeenVersion != null && lastSeenVersion !== CURRENT_VERSION;

  return useMemo(
    () => ({
      currentVersion: CURRENT_VERSION,
      lastSeenVersion,
      tourCompleted,
      markVersionSeen,
      markTourCompleted,
      isFirstRun,
      hasVersionBump,
    }),
    [lastSeenVersion, tourCompleted, markVersionSeen, markTourCompleted, isFirstRun, hasVersionBump],
  );
}
